<template>
  <section class="knowledge-panel" aria-label="题材知识关联">
    <details @toggle="onTimelineToggle">
      <summary>相关报告时间线 <small>已人工确认的关联</small></summary>
      <p v-if="loading">加载中…</p>
      <p v-if="timelineError" role="status">{{ timelineError }}</p>
      <p v-if="loaded && !reports.length && !timelineError">暂无已确认的关联报告。</p>
      <ol>
        <li v-for="item in reports" :key="`${item.report_id}-${item.section_index}`">
          <router-link :to="{ path: `/wonder/ai-report/detail/${item.report_id}`, query: { type: item.post_type === 1 ? 'evening' : 'morning', section: item.section_index } }">
            {{ item.report_date }} · {{ item.post_type === 1 ? '盘后' : '盘前' }} · {{ item.section_title }}
          </router-link>
        </li>
      </ol>
      <button v-if="hasMore" type="button" :disabled="loading" @click="loadReports(page + 1)">加载更多</button>
      <small>打开报告时按原有阅读权限展示。报告与表格各自保留独立的股票池和 K 线。</small>
    </details>
    <details v-if="canManage" class="concept-editor" @toggle="onEditorToggle">
      <summary>标准题材与别名管理</summary>
      <p>别名只填写同义名称；上下级使用上级题材，相关概念通过表格关系区分。</p>
      <label>选择标准题材
        <select v-model="selectedId" @change="selectConcept"><option value="">新建标准题材</option><option v-for="c in concepts" :key="c.id" :value="c.id">{{ c.name }}</option></select>
      </label>
      <form @submit.prevent="saveConcept">
        <label>名称<input v-model.trim="form.name" required maxlength="100"></label>
        <label>定义<textarea v-model.trim="form.definition" maxlength="5000" rows="2"></textarea></label>
        <label>别名（逗号或换行分隔）<textarea v-model="aliasesText" rows="2"></textarea></label>
        <label>上级题材<select v-model="form.parent_id"><option :value="null">无</option><option v-for="c in parentOptions" :key="c.id" :value="c.id">{{ c.name }}</option></select></label>
        <div v-for="(binding, index) in form.tables" :key="index" class="binding">
          <select v-model.number="binding.table_id" aria-label="关联表格" required><option :value="0" disabled>选择表格</option><option v-for="t in tables" :key="t.id" :value="t.id">{{ t.name }}</option></select>
          <select v-model="binding.relation" aria-label="表格关系"><option value="same">同一题材</option><option value="broader">表格覆盖更广</option><option value="related">相关题材</option></select>
          <button type="button" @click="form.tables.splice(index, 1)">移除</button>
        </div>
        <div class="actions"><button type="button" @click="form.tables.push({ table_id: tableId, relation: 'same' })">添加表格</button><button type="submit" :disabled="saving || !form.name">{{ saving ? '保存中…' : '保存标准题材' }}</button></div>
      </form>
      <p v-if="editorMessage" role="status">{{ editorMessage }}</p>
    </details>
  </section>
</template>

<script>
import { request_zzquant } from 'network/request.js'

const emptyForm = tableId => ({ name: '', definition: '', parent_id: null, tables: [{ table_id: tableId, relation: 'same' }] })
export default {
  name: 'TopicKnowledgePanel',
  props: { tableKey: { type: String, required: true }, tableId: { type: Number, required: true }, canManage: { type: Boolean, default: false } },
  data() {
    return { reports: [], page: 1, hasMore: false, loaded: false, loading: false, timelineError: '',
      concepts: [], tables: [], catalogueLoaded: false, selectedId: '', form: emptyForm(this.tableId), aliasesText: '', saving: false, editorMessage: '' }
  },
  computed: { parentOptions() { return this.concepts.filter(c => c.id !== this.selectedId) } },
  methods: {
    onTimelineToggle(event) { if (event.target.open && !this.loaded && !this.loading) this.loadReports(1) },
    onEditorToggle(event) { if (event.target.open && !this.catalogueLoaded) this.loadCatalogue() },
    async loadReports(page) {
      this.loading = true
      this.timelineError = ''
      try {
        const res = await request_zzquant({ url: `/v3/topic-knowledge/tables/${this.tableKey}/reports`, params: { page }, noRedirect: true })
        if (res.data.code !== 20000) throw new Error(res.data.message)
        this.reports = page === 1 ? res.data.data.items : [...this.reports, ...res.data.data.items]
        this.page = page
        this.hasMore = res.data.data.has_more
        this.loaded = true
      } catch (e) { this.timelineError = e.response?.status === 401 ? '登录后查看相关报告。' : (e.response?.data?.detail || '相关报告暂时无法加载。') }
      finally { this.loading = false }
    },
    async loadCatalogue() {
      try {
        const res = await request_zzquant({ url: '/v3/topic-knowledge/catalogue', noRedirect: true })
        if (res.data.code !== 20000) throw new Error(res.data.message)
        this.concepts = res.data.data.concepts
        this.tables = res.data.data.tables
        this.catalogueLoaded = true
      } catch (e) { this.editorMessage = e.response?.data?.detail || '标准题材加载失败，请先执行数据库迁移。' }
    },
    selectConcept() {
      const concept = this.concepts.find(c => c.id === this.selectedId)
      this.form = concept ? { ...concept, tables: concept.tables.map(t => ({ ...t })) } : emptyForm(this.tableId)
      this.aliasesText = concept ? concept.aliases.join('，') : ''
      this.editorMessage = ''
    },
    async saveConcept() {
      this.saving = true
      this.editorMessage = ''
      try {
        const aliases = this.aliasesText.split(/[,，\n]/).map(s => s.trim()).filter(Boolean)
        const res = await request_zzquant({ url: '/v3/topic-knowledge/concepts', method: 'POST', noRedirect: true,
          data: { id: this.selectedId || null, name: this.form.name, definition: this.form.definition, parent_id: this.form.parent_id, tables: this.form.tables, aliases } })
        if (res.data.code !== 20000) throw new Error(res.data.message)
        this.selectedId = res.data.data.id
        await this.loadCatalogue()
        this.editorMessage = '已保存，后续关联推荐将使用新配置。'
      } catch (e) { this.editorMessage = e.response?.data?.detail || e.message || '保存失败' }
      finally { this.saving = false }
    }
  }
}
</script>

<style scoped>
.knowledge-panel { padding: 10px; margin-bottom: 12px; border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-card); color: var(--text-main); font-size: 0.85rem; }
summary { cursor: pointer; font-weight: 600; } small, p { color: var(--text-muted); }
ol { margin: 10px 0; padding-left: 20px; } li { padding: 5px 0; overflow-wrap: anywhere; }
.concept-editor { margin-top: 10px; border-top: 1px solid var(--border-color); padding-top: 10px; }
label { display: block; margin: 8px 0; }
input, textarea, select { display: block; width: 100%; margin-top: 4px; padding: 6px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-card); color: var(--text-main); }
button { padding: 4px 8px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-card); color: var(--text-main); cursor: pointer; }
.binding, .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin: 8px 0; }
.binding select { flex: 1; min-width: 120px; width: auto; }.binding select:first-child { flex: 2; }
</style>
